'use client'

import { useState } from 'react'
import { AlertTriangle, ExternalLink, Plus, Trash2 } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProcessorsList } from '@/lib/api/processors'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types
// ============================================================================

const AIRWALLEX_APMS = [
  { key: 'klarna', label: 'Klarna', logoUrl: '/integrations/klarna.svg', badge: 'Buy Now Pay Later', defaultDescription: 'Pay in 4 interest-free payments', defaultCountries: ['AT', 'AU', 'BE', 'CA', 'CH', 'CZ', 'DE', 'DK', 'ES', 'FI', 'FR', 'GB', 'GR', 'IE', 'IT', 'NL', 'NO', 'NZ', 'PL', 'PT', 'SE', 'US'], presentmentCurrencies: ['AUD', 'CAD', 'CHF', 'CZK', 'DKK', 'EUR', 'GBP', 'NOK', 'NZD', 'PLN', 'SEK', 'USD'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/global/klarna', recurring: true },
  { key: 'afterpay', label: 'Afterpay / Clearpay', logoUrl: '/integrations/afterpay.svg', badge: 'Buy Now Pay Later', defaultDescription: 'Buy now, pay later in 4 installments', defaultCountries: ['AU', 'NZ', 'CA', 'US', 'GB'], presentmentCurrencies: ['AUD', 'NZD', 'CAD', 'USD', 'GBP'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/global/afterpay-clearpay', recurring: false },
  { key: 'paypal', label: 'PayPal', logoUrl: '/integrations/paypal.svg', badge: 'E-Wallet', defaultDescription: 'Pay securely with PayPal', defaultCountries: ['AU', 'US', 'GB', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'CH', 'PL', 'PT', 'IE', 'SE', 'NO', 'FI', 'DK'], presentmentCurrencies: ['AUD', 'CAD', 'CHF', 'EUR', 'GBP', 'HKD', 'NZD', 'PLN', 'SEK', 'SGD', 'USD'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/global/paypal', recurring: true },
  { key: 'skrill', label: 'Skrill', logoUrl: '/integrations/skrill.svg', badge: 'E-Wallet', defaultDescription: 'Pay securely with Skrill', defaultCountries: ['US', 'GB', 'DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'CH', 'PL', 'PT', 'IE', 'SE', 'NO', 'FI', 'DK'], presentmentCurrencies: ['EUR', 'GBP', 'USD'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/global/skrill', recurring: false },
  { key: 'bancontact', label: 'Bancontact', logoUrl: '/integrations/bancontact.svg', badge: 'Bank Transfer', defaultDescription: 'Pay with your Bancontact card', defaultCountries: ['BE'], presentmentCurrencies: ['EUR'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/emea/bancontact', recurring: false },
  { key: 'ideal', label: 'iDEAL', logoUrl: '/integrations/ideal.svg', badge: 'Online Banking', defaultDescription: 'Pay with iDEAL bank transfer', defaultCountries: ['NL'], presentmentCurrencies: ['EUR'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/emea/ideal', recurring: false },
  { key: 'twint', label: 'TWINT', logoUrl: '/integrations/twint.svg', badge: 'Mobile Payment', defaultDescription: 'Pay with TWINT mobile payment', defaultCountries: ['CH'], presentmentCurrencies: ['CHF'], docsUrl: 'https://www.airwallex.com/docs/payments/payment-methods/emea/twint-beta', recurring: false },
] as const

type ApmKey = typeof AIRWALLEX_APMS[number]['key']

interface ApmState {
  enabled: boolean
  description: string
  supportedCountries: string[]
}

interface ProcessorGroup {
  processorId: string
  methods: Record<ApmKey, ApmState>
}

interface AirwallexApmFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
  storeId: string
}

function buildDefaultMethods(): Record<ApmKey, ApmState> {
  const methods = {} as Record<ApmKey, ApmState>
  for (const apm of AIRWALLEX_APMS) {
    methods[apm.key] = {
      enabled: false,
      description: apm.defaultDescription,
      supportedCountries: [...apm.defaultCountries],
    }
  }
  return methods
}

function initProcessorGroups(defaultValues: Record<string, any>): ProcessorGroup[] {
  const settings = defaultValues.settings

  // New format: settings.processors array
  if (settings?.processors && Array.isArray(settings.processors)) {
    return settings.processors.map((group: any) => {
      const methods = buildDefaultMethods()
      if (group.methods) {
        for (const apm of AIRWALLEX_APMS) {
          const saved = group.methods[apm.key]
          if (saved) {
            methods[apm.key] = {
              enabled: saved.enabled ?? false,
              description: saved.description ?? apm.defaultDescription,
              supportedCountries: saved.supportedCountries ?? [...apm.defaultCountries],
            }
          }
        }
      }
      return { processorId: group.processorId ?? '', methods }
    })
  }

  // Legacy format: flat settings + metadata.processorId
  if (defaultValues.metadata?.processorId) {
    const methods = buildDefaultMethods()
    for (const apm of AIRWALLEX_APMS) {
      const saved = settings?.[apm.key]
      if (saved) {
        methods[apm.key] = {
          enabled: saved.enabled ?? false,
          description: saved.description ?? apm.defaultDescription,
          supportedCountries: saved.supportedCountries ?? [...apm.defaultCountries],
        }
      }
    }
    return [{ processorId: defaultValues.metadata.processorId, methods }]
  }

  return []
}

// ============================================================================
// Component
// ============================================================================

export function AirwallexApmForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
  storeId,
}: AirwallexApmFormProps) {
  const { t } = useTranslation('integrations')
  const [processorGroups, setProcessorGroups] = useState<ProcessorGroup[]>(() =>
    initProcessorGroups(defaultValues),
  )
  const [validationError, setValidationError] = useState<string | null>(null)

  const { data: allProcessors, isLoading: isLoadingProcessors } = useProcessorsList()
  const availableProcessors = (allProcessors || []).filter((p: any) => p.type === 'airwallex' && p.enabled)

  const selectedProcessorIds = new Set(processorGroups.map((g) => g.processorId).filter(Boolean))

  const addProcessorGroup = () => {
    setProcessorGroups((prev) => [
      ...prev,
      { processorId: '', methods: buildDefaultMethods() },
    ])
  }

  const removeProcessorGroup = (index: number) => {
    setProcessorGroups((prev) => prev.filter((_, i) => i !== index))
  }

  const updateGroupProcessorId = (index: number, processorId: string) => {
    setProcessorGroups((prev) =>
      prev.map((g, i) => (i === index ? { ...g, processorId } : g)),
    )
  }

  const updateGroupApmState = (groupIndex: number, key: ApmKey, updates: Partial<ApmState>) => {
    setProcessorGroups((prev) =>
      prev.map((g, i) =>
        i === groupIndex
          ? { ...g, methods: { ...g.methods, [key]: { ...g.methods[key], ...updates } } }
          : g,
      ),
    )
  }

  const isApmEnabledInOtherGroup = (apmKey: ApmKey, currentGroupIndex: number) => {
    return processorGroups.some(
      (g, i) => i !== currentGroupIndex && g.methods[apmKey]?.enabled,
    )
  }

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError(null)

    const activeGroups = processorGroups.filter(
      (g) => g.processorId && Object.values(g.methods).some((m) => m.enabled),
    )

    if (activeGroups.length === 0) {
      setValidationError('Add at least one processor with an enabled payment method.')
      return
    }

    for (const group of activeGroups) {
      if (!group.processorId) {
        setValidationError('Each processor group must have a processor selected.')
        return
      }
    }

    const processors = activeGroups.map((group) => {
      const methods: Record<string, any> = {}
      for (const apm of AIRWALLEX_APMS) {
        const state = group.methods[apm.key]
        if (state.enabled) {
          methods[apm.key] = {
            enabled: true,
            label: apm.label,
            description: state.description,
            supportedCountries: state.supportedCountries,
            presentmentCurrencies: apm.presentmentCurrencies,
            logoUrl: apm.logoUrl,
            recurring: apm.recurring,
          }
        }
      }
      return { processorId: group.processorId, methods }
    })

    await onSubmit({
      metadata: {},
      settings: { processors },
    })
  }

  return (
    <form id="integration-config-form" onSubmit={handleFormSubmit} className="space-y-6">
      {!isLoadingProcessors && availableProcessors.length === 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('airwallexApm.noProcessorTitle')}</AlertTitle>
          <AlertDescription>
            {t('airwallexApm.noProcessorDescription')}
          </AlertDescription>
        </Alert>
      )}

      {validationError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('airwallexApm.validationError')}</AlertTitle>
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      {processorGroups.map((group, groupIndex) => (
        <Card key={groupIndex}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{processorGroups.length > 1 ? t('airwallexApm.processorTitleNumbered', { number: groupIndex + 1 }) : t('airwallexApm.processorTitle')}</CardTitle>
                <CardDescription>{t('airwallexApm.processorDescription')}</CardDescription>
              </div>
              {processorGroups.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeProcessorGroup(groupIndex)}
                  disabled={isLoading}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>
                Processor
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Select
                value={group.processorId}
                onValueChange={(value) => updateGroupProcessorId(groupIndex, value)}
                disabled={isLoading || isLoadingProcessors}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('airwallexApm.selectProcessor')} />
                </SelectTrigger>
                <SelectContent>
                  {availableProcessors.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      No Airwallex processors found. Please create one first.
                    </div>
                  ) : (
                    availableProcessors.map((processor: any) => (
                      <SelectItem
                        key={processor.id}
                        value={processor.id}
                        disabled={selectedProcessorIds.has(processor.id) && processor.id !== group.processorId}
                      >
                        {processor.name}
                        {selectedProcessorIds.has(processor.id) && processor.id !== group.processorId && ' (already selected)'}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {group.processorId && (
              <div className="space-y-4">
                {AIRWALLEX_APMS.map((apm) => {
                  const state = group.methods[apm.key]
                  const disabledByOtherGroup = isApmEnabledInOtherGroup(apm.key, groupIndex)
                  return (
                    <Card key={apm.key}>
                      <CardHeader className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2 text-base">
                              <img src={apm.logoUrl} alt={apm.label} className="h-6 w-auto object-contain" />
                              {apm.label}
                              <Badge variant="outline" className="text-xs">
                                {apm.badge}
                              </Badge>
                              <a
                                href={apm.docsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground hover:text-foreground"
                                title="Airwallex docs: currency & country support"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Docs
                              </a>
                              {disabledByOtherGroup && (
                                <Badge variant="secondary" className="text-xs">
                                  Enabled in another processor
                                </Badge>
                              )}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {state.supportedCountries.length} countries · {apm.presentmentCurrencies.join(', ')} · Recurring: {apm.recurring ? 'Yes' : 'No'}
                            </CardDescription>
                          </div>
                          <Switch
                            checked={state.enabled}
                            onCheckedChange={(enabled) => updateGroupApmState(groupIndex, apm.key, { enabled })}
                            disabled={isLoading || disabledByOtherGroup}
                          />
                        </div>
                      </CardHeader>
                      {state.enabled && (
                        <CardContent className="space-y-4 pt-0">
                          <div className="space-y-2">
                            <Label htmlFor={`${groupIndex}-${apm.key}-description`}>Checkout Description</Label>
                            <Input
                              id={`${groupIndex}-${apm.key}-description`}
                              value={state.description}
                              onChange={(e) => updateGroupApmState(groupIndex, apm.key, { description: e.target.value })}
                              placeholder={apm.defaultDescription}
                              disabled={isLoading}
                            />
                            <p className="text-xs text-muted-foreground">
                              Displayed to customers at checkout when this payment method is selected
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm text-muted-foreground">Supported Countries</Label>
                            <div className="flex flex-wrap gap-1">
                              {state.supportedCountries.map((country) => (
                                <Badge key={country} variant="secondary" className="text-xs">
                                  {country}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm text-muted-foreground">Presentment currencies</Label>
                            <p className="text-xs text-muted-foreground">
                              Checkout must use one of these currencies for this method to be available.{' '}
                              <a href={apm.docsUrl} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                                See Airwallex docs
                              </a>
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {apm.presentmentCurrencies.map((currency) => (
                                <Badge key={currency} variant="outline" className="text-xs font-mono">
                                  {currency}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm text-muted-foreground">Recurring / Subscriptions</Label>
                            <Badge variant={apm.recurring ? 'default' : 'outline'} className="text-xs">
                              {apm.recurring ? 'Supported' : 'Not supported'}
                            </Badge>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={addProcessorGroup}
        disabled={isLoading || isLoadingProcessors || availableProcessors.length === 0 || selectedProcessorIds.size >= availableProcessors.length}
        className="w-full"
      >
        <Plus className="mr-2 h-4 w-4" />
        {t('airwallexApm.addProcessor')}
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{t('airwallexApm.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>{t('airwallexApm.setupStep1')}</li>
              <li>{t('airwallexApm.setupStep2')}</li>
              <li>{t('airwallexApm.setupStep3')}</li>
              <li>{t('airwallexApm.setupStep4')}</li>
              <li>{t('airwallexApm.setupStep5')}</li>
              <li>{t('airwallexApm.setupStep6')}</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
